import {
	faFileImport,
	faMessageQuestion,
	faRectangleHistoryCirclePlus,
} from "@fortawesome/pro-solid-svg-icons";
import {
	Button,
	CircleButton,
	DialogButton,
	Skeleton,
	Typography,
	VideoPlayerDialog,
	cn,
	getLocaleAsset,
	getLocaleConstants,
	separateLocale,
	useMediaQuery,
} from "@tosize/ui";
import { Tabs } from "radix-ui";
import { Fragment, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { match } from "ts-pattern";
import { useConfig } from "../../../context/ConfigContext";
import { usePostDataContext } from "../../../context/PostDataContext";
import useDimensionsForm from "../../../hooks/useDimensionsForm";
import useProducts from "../../../hooks/useProducts";
import useShapes from "../../../hooks/useShapes";
import { Step } from "../../Steps/Step";
import { ExplanationButton } from "../../ui/ExplanationButton";
import { TextDivider } from "../../ui/TextDivider";
import { Price } from "../Fields/Price";
import { Quantity } from "../Fields/Quantity";
import { USP } from "../Fields/USP";
import DimensionsForm from "../Shape/DimensionsForm";
import { FinishesField } from "../Shape/FinishesField";
import { LabelField } from "../Shape/LabelField";
import { ShapeCollection } from "../Shape/ShapeCollection";
import { TabsTrigger } from "../TabsTrigger";

export const SHAPE_DIMENSIONS_STEP_IDENTIFIER = "shape_dimensions";

export function ShapeDimensions() {
	const intl = useIntl();
	const localeConstants = getLocaleConstants(intl.locale);
	const { shapes, shape, isLoading } = useShapes();
	const { product } = useProducts();
	const { shapeModel } = useDimensionsForm(shape);
	const { workpiece, data } = usePostDataContext();
	const { settings, postcode, setActiveDialog } = useConfig();
	const isSmallDevice = useMediaQuery({ max: "lg" });
	const [shapeTabValue, setShapeTabValue] = useState("edit");

	const selectedValue = useMemo(() => {
		const dimensionsString = shapeModel?.getDimensionsString(workpiece.shape_dimensions, intl);

		if (shape?.name && dimensionsString) {
			return `${shape?.name} | ${dimensionsString}`;
		}
	}, [workpiece.shape_dimensions, intl, shape?.name, shapeModel]);

	const video = match(separateLocale(intl.locale).language)
		.with("nl", () => ({ identifier: "1003530256", time: 177 }))
		.with("de", () => ({ identifier: "1038150968", time: undefined }))
		.with("fr", () => ({ identifier: "1039430249", time: undefined }))
		.otherwise(() => undefined);

	return (
		<Step
			title={intl.formatMessage({
				defaultMessage: "Vorm & Afmetingen",
			})}
			value={SHAPE_DIMENSIONS_STEP_IDENTIFIER}
			disabled={!product}
			selected={!!selectedValue}
			selectedValue={selectedValue ?? shape?.name}
		>
			{isLoading || !data.product ? (
				<LoaderComponent />
			) : !postcode && localeConstants.postcode ? (
				<div className="mb-4 flex flex-col">
					<Typography variant="paragraph-bold" className="mb-2">
						{intl.formatMessage({ defaultMessage: "Verzendopties in jouw regio" })}
					</Typography>

					<Typography className="mb-4">
						{intl.formatMessage({
							defaultMessage:
								"Vul de postcode van het bezorgadres in zodat de webshop de juiste afmetingen en verzendkosten berekent voor jouw bestelling",
						})}
					</Typography>

					<Button onClick={() => setActiveDialog("postcode")}>
						{intl.formatMessage({ defaultMessage: "Verzendopties instellen" })}
					</Button>
				</div>
			) : (
				<>
					<div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
						<div>
							<ExplanationButton
								title={`${intl.formatMessage(
									{
										defaultMessage: "Kies uit {value} verschillende vormen",
									},
									{ value: shapes?.length ?? 0 },
								)}:`}
							>
								{video && (
									<VideoPlayerDialog
										player={{
											variant: "vimeo",
											id: video.identifier,
											time: video.time,
											autoPlay: true,
											title: intl.formatMessage({
												defaultMessage: "Configurator Uitleg",
											}),
										}}
										title={intl.formatMessage({
											defaultMessage: "Hulp nodig bij bestellen?",
										})}
									>
										<CircleButton component="button" className="text-sm" />
									</VideoPlayerDialog>
								)}
							</ExplanationButton>
						</div>

						<DialogButton
							title={intl.formatMessage({ defaultMessage: "Hoe werkt het?" })}
							content={
								<div className="prose">
									<h3>{intl.formatMessage({ defaultMessage: "Minimale afmetingen" })}</h3>
									<p>
										{intl.formatMessage({
											defaultMessage:
												"De minimale afmetingen die wij kunnen zagen zijn 5,0 x 5,0 cm. Bij een andere vorm dan een rechthoek of vierkant wijken de minimale afmetingen af. De specifieke afmetingen vind je in de configurator, maar over het algemeen geldt dat minimaal één zijde een lengte van minimaal 25,0 cm moet hebben. Bij te kleine oppervlakte heeft de zaag of frees niet genoeg houvast om het item correct te kunnen realiseren.",
										})}
									</p>
									<p>
										{intl.formatMessage({
											defaultMessage:
												"Ook wanneer je een afwerking kiest, kan je afwijkende minimale afmetingen tegenkomen. Ook hier is de reden de kwaliteit. Deze varieert per afwerking en kan je terugvinden in de configurator bij het specifieke product.",
										})}
									</p>
									<h3>{intl.formatMessage({ defaultMessage: "Maximale afmetingen" })}</h3>
									<p>
										{intl.formatMessage(
											{
												defaultMessage:
													"De maximale afmetingen zijn afhankelijk van de beschikbare maximale plaatafmeting en de maximale transportafmeting. De maximale plaatafmeting kan je bij het product, in de product configurator, terugvinden. Daarnaast houden we ook direct rekening met de maximale transport lengte. We kunnen tot een maximale lengte van {value} cm de panelen transporteren.",
											},
											{ value: settings.get("shipping/sizes/general/max_length") },
										)}
									</p>
								</div>
							}
						>
							<Button
								variant="link"
								className="text-sm"
								starticon={isSmallDevice ? faMessageQuestion : undefined}
							>
								{intl.formatMessage({ defaultMessage: "Hoe werkt het?" })}
							</Button>
						</DialogButton>
					</div>

					{shapes && shapes.length <= 2 ? (
						<>
							<ReducedTabs />
							<CurrentShapeContent />
						</>
					) : (
						<Tabs.Root
							defaultValue="edit"
							value={shapeTabValue}
							onValueChange={setShapeTabValue}
							className="space-y-4"
						>
							<ShapeTabList />
							<Tabs.Content value="edit" tabIndex={-1} className="space-y-4 focus:outline-none">
								<CurrentShapeContent />
							</Tabs.Content>
							<Tabs.Content value="collection" tabIndex={-1} className="focus:outline-none">
								<ShapeCollection setShapeTabValue={setShapeTabValue} />
							</Tabs.Content>
						</Tabs.Root>
					)}
				</>
			)}
		</Step>
	);
}

function ReducedTabs() {
	const { shapes, shape, setCurrentShape } = useShapes();

	useEffect(() => {
		if (!shape && shapes?.length === 1 && shapes[0]) {
			setCurrentShape({ shape: shapes[0].code, scroll: true });
		}
	}, [shape, shapes, setCurrentShape]);

	if (!shapes || shapes.length < 1) {
		return null;
	}

	return (
		<div className="grid grid-cols-2 gap-x-2 sm:grid-cols-[1fr,4rem,1fr] sm:gap-x-0 lg:grid-cols-2 lg:gap-x-2 xl:grid-cols-[1fr,4rem,1fr] xl:gap-x-0">
			{shapes.map((item, index) => (
				<Fragment key={item.code}>
					<button
						type="button"
						onClick={() => setCurrentShape({ shape: item.code, scroll: true })}
						className={cn(
							"flex flex-col items-center justify-center rounded-xl border-2 p-4 text-center",
							"focus:outline-none focus-visible:border-secondary focus-visible:bg-white focus-visible:text-dark motion-safe:transition-colors",
							"hover:border-secondary hover:bg-white hover:text-dark",
							{
								"border-shades bg-white": item.code !== shape?.code,
								"border-secondary bg-secondary text-white": item.code === shape?.code,
							},
						)}
					>
						<img src={item.images.general} alt={item.name} height={62.5} width={125} />
						<span className="mt-2 leading-none">{item.name}</span>
					</button>

					{index + 1 !== shapes.length && (
						<TextDivider variant="vertical" className="hidden sm:flex lg:hidden xl:flex">
							<FormattedMessage defaultMessage="Of" />
						</TextDivider>
					)}
				</Fragment>
			))}
		</div>
	);
}

function CurrentShapeContent() {
	const intl = useIntl();
	const { setActiveDialog } = useConfig();

	return (
		<>
			<DimensionsForm />

			<LabelField />
			<FinishesField />

			<div className="h-px w-full bg-shades-medium-light" />
			<div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
				<Button
					className="text-sm"
					variant="link"
					starticon={faRectangleHistoryCirclePlus}
					onClick={() => setActiveDialog("importProducts_list")}
				>
					{intl.formatMessage({
						defaultMessage: "Meerdere afmetingen invoeren",
					})}
				</Button>

				<Button
					className="text-sm"
					variant="link"
					starticon={faFileImport}
					onClick={() => setActiveDialog("importProducts_file")}
				>
					{intl.formatMessage({
						defaultMessage: "CSV importeren",
					})}
				</Button>
			</div>
			<div className="h-px w-full bg-shades-medium-light" />

			<USP />
			<Price />
			<Quantity />
			{/* <FinishesNextField /> */}
		</>
	);
}

function ShapeTabList() {
	const intl = useIntl();
	const { shape, shapes } = useShapes();

	const currentShape = useMemo(
		() => shapes?.find((item) => item.code === shape?.code),
		[shape?.code, shapes],
	);

	if (!currentShape) {
		return null;
	}

	return (
		// sm:grid-cols-[1fr,4rem,1fr] lg:grid-cols-2 xl:grid-cols-[1fr,4rem,1fr] gap-x-2 sm:gap-x-0  lg:gap-x-2  xl:gap-x-0
		<Tabs.List className="grid grid-cols-2 gap-4">
			<TabsTrigger
				variant="shapes"
				value="edit"
				title={currentShape.name}
				image={currentShape.images.general}
			/>
			<TabsTrigger
				variant="shapes"
				value="collection"
				title={intl.formatMessage({ defaultMessage: "Kies een andere vorm" })}
				image={getLocaleAsset("shapes/shapes_collection.png")}
			/>
		</Tabs.List>
	);
}

function LoaderComponent() {
	return (
		<div>
			<div className="grid grid-cols-2 gap-x-2 gap-y-6 sm:gap-x-4 lg:gap-x-2 xl:gap-x-4">
				{Array.from({ length: 2 }).map((_value, index) => (
					<Skeleton className="h-[122.75px] w-full" key={index} />
				))}

				{Array.from({ length: 2 }).map((_value, index) => (
					<div key={index}>
						<Skeleton className="mb-1 h-[24px] w-3/5" />
						<Skeleton className="h-[42px] w-full" />
					</div>
				))}
			</div>

			<div className="!mt-6 border-shades-light border-t pt-4">
				<Skeleton className="mb-1 h-[24px] w-3/5" />
				<Skeleton className="h-[42px] w-full" />
				<Skeleton className="mt-[5px] h-[17px] w-4/5" />
			</div>

			<Skeleton className="mt-4 h-[80px] w-full" />

			<Skeleton className="mt-4 h-[52px] w-full" />
			<Skeleton className="mx-auto mt-[11px] h-[17px] w-4/5" />
			<Skeleton className="mx-auto my-4 h-[24px] w-[24px]" />
			<Skeleton className="mx-auto h-[24px] w-2/5" />
		</div>
	);
}
